import { SearchProvider, SearchProviderType } from '@/types/searchProvider';
import { StandardSearchProvider } from './StandardSearchProvider';
import { N8nAISearchProvider } from './N8nAISearchProvider';
import { supabase } from '@/integrations/supabase/client';

interface SearchProviderCacheEntry {
  providerType: SearchProviderType;
  timestamp: number;
  version: string;
}

export class SearchProviderFactory {
  private static cachedProvider: SearchProvider | null = null;
  private static cachedProviderType: SearchProviderType | null = null;
  
  // Cache configuration
  private static readonly CACHE_KEY = 'aizen_search_provider_setting';
  private static readonly CACHE_VERSION = '1.0.0';
  private static readonly DEFAULT_CACHE_TTL_MINUTES = 5; // Default 5 minutes
  
  /**
   * Get cache TTL from settings table (in minutes) or use default
   */
  private static async getCacheTTLMinutes(): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'search_provider_cache_ttl_minutes')
        .single();
        
      if (error || !data?.value) {
        return this.DEFAULT_CACHE_TTL_MINUTES;
      }
      
      const ttlMinutes = parseInt(data.value, 10);
      
      // Validate range (0-1440 minutes = no cache to 24 hours)
      if (isNaN(ttlMinutes) || ttlMinutes < 0 || ttlMinutes > 1440) {
        console.warn(`Invalid cache TTL setting: ${data.value}, using default ${this.DEFAULT_CACHE_TTL_MINUTES} minutes`);
        return this.DEFAULT_CACHE_TTL_MINUTES;
      }
      
      return ttlMinutes;
    } catch (error) {
      console.error('Error fetching cache TTL setting:', error);
      return this.DEFAULT_CACHE_TTL_MINUTES;
    }
  }
  
  /**
   * Load cached search provider setting from localStorage
   */
  private static async loadProviderSettingFromCache(): Promise<SearchProviderType | null> {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const cacheEntry: SearchProviderCacheEntry = JSON.parse(cached);
      
      // Check version compatibility
      if (cacheEntry.version !== this.CACHE_VERSION) {
        console.log('Search provider cache version mismatch, invalidating cache');
        this.clearSettingCache();
        return null;
      }

      // Get current TTL setting and check if cache is still valid
      const ttlMinutes = await this.getCacheTTLMinutes();
      
      // If TTL is 0, disable caching entirely (always refresh)
      if (ttlMinutes === 0) {
        console.log('Cache TTL set to 0, invalidating cache (caching disabled)');
        this.clearSettingCache();
        return null;
      }
      
      const ttlMs = ttlMinutes * 60 * 1000;
      const now = Date.now();
      
      if (now - cacheEntry.timestamp > ttlMs) {
        console.log(`Search provider cache expired (TTL: ${ttlMinutes} minutes), invalidating cache`);
        this.clearSettingCache();
        return null;
      }

      console.log(`Using cached search provider setting: ${cacheEntry.providerType} (TTL: ${ttlMinutes} minutes)`);
      return cacheEntry.providerType;
    } catch (error) {
      console.error('Error loading search provider cache:', error);
      this.clearSettingCache();
      return null;
    }
  }
  
  /**
   * Save search provider setting to localStorage cache
   */
  private static saveProviderSettingToCache(providerType: SearchProviderType): void {
    try {
      const cacheEntry: SearchProviderCacheEntry = {
        providerType,
        timestamp: Date.now(),
        version: this.CACHE_VERSION,
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheEntry));
      console.log(`Cached search provider setting: ${providerType}`);
    } catch (error) {
      console.error('Error saving search provider to cache:', error);
      // Graceful degradation: continue without caching
    }
  }
  
  /**
   * Clear the search provider setting cache
   */
  public static clearSettingCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('Search provider setting cache cleared');
    } catch (error) {
      console.error('Error clearing search provider cache:', error);
      // Graceful degradation: continue silently
    }
  }
  
  /**
   * Get the current search provider setting from cache or database
   */
  private static async getSearchProviderSetting(): Promise<SearchProviderType> {
    // Try cache first
    const cachedSetting = await this.loadProviderSettingFromCache();
    if (cachedSetting) {
      return cachedSetting;
    }
    
    // Fall back to database
    try {
      console.log('Fetching search provider setting from database...');
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'search_provider')
        .single();
        
      if (error) {
        console.warn('Failed to fetch search_provider setting, using default:', error);
        return 'search-standard';
      }
      
      const providerType = data?.value as SearchProviderType;
      
      // Validate the provider type
      if (providerType === 'search-standard' || providerType === 'n8n-search-hybrid-standard-ai') {
        // Cache the valid setting
        this.saveProviderSettingToCache(providerType);
        return providerType;
      }
      
      console.warn(`Invalid search provider type: ${providerType}, using default`);
      const defaultProvider: SearchProviderType = 'search-standard';
      this.saveProviderSettingToCache(defaultProvider);
      return defaultProvider;
      
    } catch (error) {
      console.error('Error fetching search provider setting:', error);
      const defaultProvider: SearchProviderType = 'search-standard';
      this.saveProviderSettingToCache(defaultProvider);
      return defaultProvider;
    }
  }
  
  /**
   * Create a search provider instance based on the cached or database setting
   */
  static async createProvider(): Promise<SearchProvider> {
    const providerType = await this.getSearchProviderSetting();
    
    // Return cached provider if it matches the current setting
    if (this.cachedProvider && this.cachedProviderType === providerType) {
      return this.cachedProvider;
    }
    
    // Create new provider instance
    let provider: SearchProvider;
    
    switch (providerType) {
      case 'n8n-search-hybrid-standard-ai':
        provider = new N8nAISearchProvider();
        console.log('Created N8n AI Search Provider');
        break;
        
      case 'search-standard':
      default:
        provider = new StandardSearchProvider();
        console.log('Created Standard Search Provider');
        break;
    }
    
    // Cache the provider
    this.cachedProvider = provider;
    this.cachedProviderType = providerType;
    
    return provider;
  }
  
  /**
   * Clear the cached provider (useful for testing or forcing refresh)
   */
  static clearCache(): void {
    this.cachedProvider = null;
    this.cachedProviderType = null;
    this.clearSettingCache();
  }
  
  /**
   * Get available provider types
   */
  static getAvailableProviders(): SearchProviderType[] {
    return ['search-standard', 'n8n-search-hybrid-standard-ai'];
  }
  
  /**
   * Update the search provider setting in database and clear caches
   */
  static async updateProviderSetting(providerType: SearchProviderType): Promise<void> {
    try {
      const { error } = await supabase
        .from('settings')
        .update({ value: providerType })
        .eq('key', 'search_provider');
        
      if (error) {
        throw error;
      }
      
      // Update cache with new setting
      this.saveProviderSettingToCache(providerType);
      
      // Clear provider cache to force recreation on next use
      this.cachedProvider = null;
      this.cachedProviderType = null;
      
      console.log(`Search provider updated to: ${providerType}`);
      
    } catch (error) {
      console.error('Failed to update search provider setting:', error);
      throw error;
    }
  }
} 